/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './collections.stache';
import FeedService from 'scripts/services/base-feed-service';
import dtm from 'scripts/services/dtm';
import can from 'can';
import Swiper from 'swiper';
import urlUtil from 'scripts/utils/url';
import appContext from 'scripts/app-context';
import DomEventsManager from 'scripts/dom-events-manager';
import _ from 'lodash';

const collectionFeedUrl = window.globalSettings.feeds.historyCollectionFeed;

const encodeCollectionIdentifier = (decoded) =>
    decoded.replace(/\s/g, '-').toLowerCase();

Component.extend({
    tag: 'history-experience-collections',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            fetching: {
                type: 'boolean',
                value: true,
            },
            selectedCollection: {
                Type: can.Map,
                Value: can.Map,
            },
            fullCollection: {
                Type: can.Map,
                Value: can.Map,
            },
            fullModalCollection: {
                Type: can.Map,
                Value: can.Map,
            },
            collection: {
                Type: can.Map,
                Value: can.Map,
            },
            collectionHashObjects: {
                Type: can.List,
                Value: can.List,
            },
            isMobile: {
                get() {
                    return (
                        ['small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
            dropdownIsHidden: {
                type: 'boolean',
                value: true,
            },
            activeCardIndex: {
                type: 'number',
                value: null,
            },
        },
        prepareHashObjects: function() {
            const hashObjects = this.attr('collectionTags')
                .serialize()
                .map((tag) => {
                    return {
                        name: tag,
                        hash: encodeCollectionIdentifier(tag),
                    };
                });
            this.attr('collectionHashObjects', hashObjects);
        },
        getCurrentHashObject: function() {
            const collectionHashObject = this.attr('collectionHashObjects')
                .serialize()
                .find((hashObject) => can.route.attr(hashObject.hash));
            if (collectionHashObject) {
                return {
                    key: collectionHashObject.hash,
                    value: can.route.attr(collectionHashObject.hash),
                    isCard: true,
                };
            } else if (can.route.attr('identifier')) {
                const hashKeyValue = can.route.attr('identifier').split('=');
                const isCardOpened = hashKeyValue.length > 1;
                return {
                    key: hashKeyValue[0],
                    value: isCardOpened ? hashKeyValue[1] : null,
                    isCard: isCardOpened,
                };
            }
            return {
                value: urlUtil.getHash().slice(1),
                isCard: false,
            };
        },
        selectCollection: function(collectionHash) {
            if (!collectionHash) {
                const firstTagHash = this.attr('collectionHashObjects')[0].hash;
                this.selectCollection(firstTagHash);
                return;
            }

            const collectionHashObject = this.attr('collectionHashObjects')
                .serialize()
                .find((hashObject) => hashObject.hash === collectionHash);
            this.attr('selectedCollection.name', collectionHashObject.name);
            this.attr('selectedCollection.hash', collectionHashObject.hash);
        },
    },
    events: {
        inserted() {
            this.viewModel.prepareHashObjects();
            this.registerRoutes();
            DomEventsManager.subscribe(this);
            this.registerModal();
            this.registerFeedService();

            can.$('body').on('click', (e) => {
                if (
                    !can
                        .$(e.target)
                        .closest('.s-history-collections-header__item').length
                ) {
                    this.viewModel.attr('dropdownIsHidden', true);
                }
            });

            this.initModalEvents();
            this.selectCollection();
            this.fetchData();
        },

        registerRoutes() {
            this.viewModel
                .attr('collectionHashObjects')
                .serialize()
                .forEach((hashObject) =>
                    can.route(`${hashObject.hash}=:cardIdentifier`),
                );
            can.route(':identifier');
            can.route.ready();
        },

        doRoute(routeAttr) {
            can.route.attr(routeAttr, true);
        },

        selectCollection() {
            const hashObject = this.viewModel.getCurrentHashObject();
            this.viewModel.selectCollection(hashObject.key);

            if (!hashObject.key) {
                const collection = this.viewModel.attr(
                    'selectedCollection.hash',
                );
                this.doRoute({ identifier: collection });
            }
        },

        fetchData() {
            const collection = this.viewModel.attr('selectedCollection.hash');
            this.viewModel.attr('fetching', true);
            this.feedService.fetch({ collection: collection });
        },

        registerFeedService() {
            this.feedService = new FeedService({
                feedUrl: collectionFeedUrl,
                fromCache: true,
                autoUpdate: false,
            }).on('data', (ev, data) => {
                can.batch.start();
                this.viewModel.attr('fullCollection', data);
                this.viewModel.attr('fullModalCollection', data);
                let cards = this.viewModel.attr('fullCollection.cards');
                if (cards && cards.length) {
                    this.viewModel.attr('collection', {
                        cards: cards.splice(0, 9),
                    });
                }
                can.batch.stop();
                this.viewModel.attr('fetching', false);

                const currentHashObject = this.viewModel.getCurrentHashObject();
                if (currentHashObject.isCard && currentHashObject.value) {
                    this.showModalSlider(parseInt(currentHashObject.value, 10));
                }
            });
        },

        registerModal() {
            this.modalDialog = this.element.find('timeline-modal-dialog');
            const modalDialogViewModel = this.modalDialog.viewModel();
            modalDialogViewModel.on('modalClosed', () => {
                const currentHashObject = this.viewModel.getCurrentHashObject();
                this.doRoute({ identifier: currentHashObject.key });
                this.viewModel.attr('activeCardIndex', null);
            });
        },

        showModalSlider(initialSlide) {
            const modalContainer = this.element.find('.c-history-collections');
            if (this.modalDialog && this.modalDialog.length) {
                this.modalDialog.viewModel().show(modalContainer);
                this.initModalSlider(initialSlide);
            }
        },

        initModalEvents() {
            const modalDialogViewModel = this.element
                .find('timeline-modal-dialog')
                .viewModel();

            modalDialogViewModel.on('modalOpened', () => {
                this.viewModel.attr('isModalShown', true);

                if (this.modalSwiper) {
                    this.modalSwiper.params.hashNavigation = {
                        replaceState: true,
                    };
                }
            });

            modalDialogViewModel.on('modalClosed', () => {
                this.viewModel.attr('isModalShown', false);

                if (this.modalSwiper) {
                    this.modalSwiper.params.hashNavigation = false;
                }
            });
        },

        destroyModalSlider() {
            if (this.modalSwiper) {
                this.modalSwiper.destroy(false, true);
                this.modalSwiper = null;
            }
        },

        initModalSlider(initialSlide) {
            this.destroyModalSlider();
            let swiperContainer = can.$(
                '.s-history-experience-collections-modal-slider',
            );
            let slideNumber = swiperContainer.find('.swiper-slide').length;

            if (slideNumber > 1 && !this.modalSwiper) {
                this.modalSwiper = new Swiper(swiperContainer, {
                    init: false,
                    initialSlide: ~~initialSlide,
                    direction: this.viewModel.attr('isMobile')
                        ? 'vertical'
                        : 'horizontal',
                    spaceBetween: 0,
                    mousewheel: true,
                    keyboard: {
                        enabled: true,
                    },
                    centeredSlides: true,
                    slidesPerView: 1,
                    simulateTouch: true,
                    hashNavigation: {
                        replaceState: true,
                    },
                    navigation: {
                        nextEl: swiperContainer.find('.swiper-button--next'),
                        prevEl: swiperContainer.find('.swiper-button--prev'),
                    },
                });

                this.modalSwiper.on('init', () => {
                    if (initialSlide !== 0) {
                        return;
                    }

                    const card = this.viewModel.attr(
                        `fullModalCollection.cards.0`,
                    );
                    const tag = card.collectionTags
                        ? card.collectionTags[0].toLowerCase()
                        : '';

                    dtm.trackHistoryContentView(
                        card.title,
                        card.cardType,
                        card.cardYear,
                        tag,
                    );
                });

                this.modalSwiper.on('slideChangeTransitionStart', () => {
                    this.viewModel.attr(
                        'activeCardIndex',
                        this.modalSwiper.activeIndex,
                    );
                });

                this.modalSwiper.on('slideChangeTransitionEnd', () => {
                    const card = this.viewModel.attr(
                        `fullModalCollection.cards.${this.modalSwiper.activeIndex}`,
                    );
                    const tag = card.collectionTags
                        ? card.collectionTags[0].toLowerCase()
                        : '';

                    dtm.trackHistoryContentView(
                        card.title,
                        card.cardType,
                        card.cardYear,
                        tag,
                    );
                });

                this.modalSwiper.init();
            } else if (this.modalSwiper) {
                this.modalSwiper.slideTo(initialSlide, 0);
            }

            this.viewModel.attr('activeCardIndex', initialSlide);
            this.doRoute({
                [this.viewModel.attr('selectedCollection.hash')]: initialSlide,
            });
        },

        reinitSliders() {
            let activeCardIndex = this.viewModel.attr('activeCardIndex');
            this.destroyModalSlider();
            if (this.viewModel.attr('isModalShown')) {
                this.initModalSlider(activeCardIndex);
            }
        },

        updateSlider() {
            if (this.modalSwiper) {
                this.modalSwiper.update();
            }
        },

        onWindowResize: function() {
            this.debouncedWindowResize();
        },

        debouncedWindowResize: _.debounce(function() {
            this.reinitSliders();
        }, 500),

        onWindowScroll: _.throttle(function() {
            this.throttledWindowScroll();
        }, 100),

        throttledWindowScroll() {
            if (
                (window.innerHeight * 2 + window.pageYOffset ||
                    window.scrollY) >= document.body.offsetHeight
            ) {
                this.addCards();
            }
        },

        addCards() {
            if (this.viewModel.attr('fullCollection.cards.length')) {
                this.viewModel
                    .attr('collection.cards')
                    .push(
                        ...this.viewModel
                            .attr('fullCollection.cards')
                            .splice(0, 3),
                    );
                this.updateSlider();
            }
        },

        '.s-history-collections-header-dropdown__item-selected click'() {
            this.viewModel.attr(
                'dropdownIsHidden',
                !this.viewModel.attr('dropdownIsHidden'),
            );
        },

        '.s-history-collections-list__item click'(element) {
            const parent = element.closest('.s-history-collections-content');
            parent.find('.s-history-collections-list').addClass('is-hidden');
            parent
                .find('.s-history-collections-cards')
                .removeClass('is-hidden');
        },

        '.s-history-collections-header-dropdown-list__item-text click'(
            element,
        ) {
            this.viewModel.attr('dropdownIsHidden', true);

            const collection = element.data('collection');
            this.viewModel.selectCollection(collection);
            this.doRoute({ identifier: collection });

            this.destroyModalSlider();

            dtm.trackHistoryTimelineCollectionSelection(
                collection.replace('-', ' '),
            );
            this.fetchData();
        },

        '.s-history-collections-cards__item click'(element) {
            this.showModalSlider(element.data('index'));
            const videoName = element.data('video-name');
            if (videoName && !this.viewModel.attr('isMobile')) {
                dtm.trackHistoryVideoLauch(
                    videoName,
                    'History Collection Content',
                );
            }
        },
    },
    helpers: {
        collectionHash(options) {
            return encodeCollectionIdentifier(options.context);
        },
    },
});
